import { generateOrderKeys } from "../../../erd-editor-schema/dist/orderKey.js";
import { query } from "../../../erd-editor-schema/dist/query/index.js";
import { DictionaryFields, DictionaryKind } from "./actions.js";
import { getLiveDomainName, isListed } from "./membership.js";
import { changeColumnLogicalNameAction } from "../table-column/atom.actions.js";
import { toDomainTypeActions } from "./domainTypes.js";
import { orderedDictionaryRows } from "./dictionaryIndex.js";
import { dictionaryIds, hasDictionaryKind, removeDictionaryAction } from "./atom.actions.js";
import { SelectType } from "../editor/state.js";
import { changeTableLogicalNameAction } from "../table/atom.actions.js";
import { toColumnNameWrites, toTableNameWrites } from "./derivedNames.js";
import { placeOrderKeys } from "./orderKeys.js";
import { toDictionaryCommit, toDictionaryImport } from "./entryWrites.js";
//#region src/engine/modules/dictionary/generator.actions.ts
var tableOf = (state, id) => query(state.collections).collection("tableEntities").selectById(id);
var columnOf = (state, id) => query(state.collections).collection("tableColumnEntities").selectById(id);
function* commit(state, kind, changes) {
	if (!hasDictionaryKind(kind)) return;
	const { actions } = toDictionaryCommit(state, kind, changes);
	if (actions.length) yield actions;
}
var addDictionaryEntryAction$ = (kind, values = {}) => function* (state) {
	yield* commit(state, kind, [{ values }]);
};
function insertIndexOf(rows, anchor) {
	const index = anchor ? rows.findIndex(({ id }) => id === anchor.id) : -1;
	if (!anchor || index === -1) return rows.length;
	return anchor.place === "after" ? index + 1 : index;
}
var insertDictionaryEntriesAction$ = (kind, count, anchor = null) => function* (state) {
	if (!hasDictionaryKind(kind) || count <= 0) return;
	const rows = orderedDictionaryRows(state, kind);
	const { keys, rekeyed } = placeOrderKeys(rows, insertIndexOf(rows, anchor), count);
	yield* commit(state, kind, [...rekeyed.map(({ id, order }) => ({
		id,
		values: { order }
	})), ...keys.map((order) => ({ values: { order } }))]);
};
var DictionarySortDirection = {
	ascending: "ascending",
	descending: "descending"
};
var sortDictionaryAction$ = (kind, field, direction, locale) => function* (state) {
	if (!hasDictionaryKind(kind)) return;
	if (!DictionaryFields[kind].includes(field)) return;
	const collator = new Intl.Collator(locale, { numeric: true });
	const sign = direction === DictionarySortDirection.descending ? -1 : 1;
	const textOf = (row) => kind === DictionaryKind.term && field === "domainId" ? getLiveDomainName(state, Reflect.get(row, "domainId")) : Reflect.get(row, field);
	const rows = orderedDictionaryRows(state, kind).map((row, index) => ({
		row,
		index,
		text: textOf(row)
	}));
	rows.sort((a, b) => {
		const emptyA = a.text === "";
		if (emptyA !== (b.text === "")) return emptyA ? 1 : -1;
		return sign * collator.compare(a.text, b.text) || a.index - b.index;
	});
	if (rows.every(({ index }, at) => index === at)) return;
	const keys = generateOrderKeys(null, null, rows.length);
	yield* commit(state, kind, rows.flatMap(({ row }, at) => row.order === keys[at] ? [] : [{
		id: row.id,
		values: { order: keys[at] }
	}]));
};
var updateDictionaryEntryAction$ = (kind, id, values) => function* (state) {
	yield* commit(state, kind, [{
		id,
		values
	}]);
};
var removeDictionaryEntriesAction$ = (kind, ids) => function* (state) {
	if (!hasDictionaryKind(kind)) return;
	const listed = dictionaryIds(state, kind);
	const named = new Set(ids.filter((id) => isListed(listed, id)));
	if (!named.size) return;
	const removed = [];
	for (let index = listed.length - 1; index >= 0; index--) if (named.has(listed[index])) removed.push(listed[index]);
	yield removed.map((id) => removeDictionaryAction({
		kind,
		id
	}));
};
var commitDictionaryCellsAction$ = (kind, changes) => function* (state) {
	yield* commit(state, kind, changes);
};
var importDictionaryAction$ = (kind, rows) => function* (state) {
	if (!hasDictionaryKind(kind)) return;
	const { actions } = toDictionaryImport(state, kind, rows);
	if (actions.length) yield actions;
};
function columnNameActions(state, names) {
	const actions = [];
	const links = [];
	const ledger = /* @__PURE__ */ new Map();
	for (const { tableId, columnId, logicalName } of names) {
		const column = columnOf(state, columnId);
		if (!column) continue;
		const value = logicalName ?? column.logicalName;
		if (logicalName !== null && logicalName !== column.logicalName) actions.push(changeColumnLogicalNameAction({
			tableId,
			id: columnId,
			value
		}));
		const writes = toColumnNameWrites(state, tableId, column, value, false, ledger);
		actions.push(...writes.actions);
		if (writes.link) links.push(writes.link);
	}
	if (links.length) actions.push(...toDomainTypeActions(state, { links }));
	return actions;
}
function tableNameActions(state, tableId, logicalName) {
	const table = tableOf(state, tableId);
	if (!table) return [];
	const value = logicalName ?? table.logicalName;
	const actions = logicalName !== null && logicalName !== table.logicalName ? [changeTableLogicalNameAction({
		id: tableId,
		value
	})] : [];
	actions.push(...toTableNameWrites(state, table, value));
	return actions;
}
var setColumnLogicalNameAction$ = (tableId, columnId, value) => function* (state) {
	const actions = columnNameActions(state, [{
		tableId,
		columnId,
		logicalName: value
	}]);
	if (actions.length) yield actions;
};
var setColumnLogicalNamesAction$ = (names) => function* (state) {
	const actions = columnNameActions(state, names.map(({ tableId, columnId, value }) => ({
		tableId,
		columnId,
		logicalName: value
	})));
	if (actions.length) yield actions;
};
var setTableLogicalNameAction$ = (tableId, value) => function* (state) {
	const actions = tableNameActions(state, tableId, value);
	if (actions.length) yield actions;
};
var deriveColumnNameAction$ = (tableId, columnId) => function* (state) {
	const actions = columnNameActions(state, [{
		tableId,
		columnId,
		logicalName: null
	}]);
	if (actions.length) yield actions;
};
var deriveTableNameAction$ = (tableId) => function* (state) {
	const actions = tableNameActions(state, tableId, null);
	if (actions.length) yield actions;
};
var RederiveScope = {
	column: "column",
	table: "table",
	selection: "selection",
	document: "document"
};
function rederivedTableIds(state, target) {
	const { doc, editor } = state;
	const listed = new Set(doc.tableIds);
	const isInDoc = (id) => listed.has(id);
	switch (target.scope) {
		case RederiveScope.table: return isInDoc(target.tableId) ? [target.tableId] : [];
		case RederiveScope.selection: return Object.entries(editor.selectedMap).filter(([id, type]) => type === SelectType.table && isInDoc(id)).map(([id]) => id);
		case RederiveScope.document: return [...doc.tableIds];
		default: return [];
	}
}
var rederiveNamesAction$ = (target) => function* (state) {
	const actions = [];
	const links = [];
	const ledger = /* @__PURE__ */ new Map();
	const deriveColumn = (tableId, columnId) => {
		const column = columnOf(state, columnId);
		if (!column) return;
		const writes = toColumnNameWrites(state, tableId, column, column.logicalName, true, ledger);
		actions.push(...writes.actions);
		if (writes.link) links.push(writes.link);
	};
	if (target.scope === RederiveScope.column) deriveColumn(target.tableId, target.columnId);
	for (const tableId of rederivedTableIds(state, target)) {
		const table = tableOf(state, tableId);
		if (!table) continue;
		actions.push(...toTableNameWrites(state, table, table.logicalName, true));
		for (const columnId of table.columnIds) deriveColumn(tableId, columnId);
	}
	if (links.length) actions.push(...toDomainTypeActions(state, { links }));
	if (actions.length) yield actions;
};
var setColumnDomainAction$ = (tableId, columnId, domainId) => function* (state) {
	if (!columnOf(state, columnId)) return;
	if (domainId !== "" && !isListed(state.doc.domainIds, domainId)) return;
	const actions = toDomainTypeActions(state, { links: [{
		tableId,
		id: columnId,
		value: domainId
	}] });
	if (actions.length) yield actions;
};
var actions$ = {
	addDictionaryEntryAction$,
	updateDictionaryEntryAction$,
	removeDictionaryEntriesAction$,
	importDictionaryAction$,
	commitDictionaryCellsAction$,
	setColumnLogicalNameAction$,
	setTableLogicalNameAction$,
	deriveColumnNameAction$,
	deriveTableNameAction$,
	rederiveNamesAction$,
	setColumnDomainAction$
};
//#endregion
export { DictionarySortDirection, RederiveScope, actions$, addDictionaryEntryAction$, commitDictionaryCellsAction$, deriveColumnNameAction$, deriveTableNameAction$, importDictionaryAction$, insertDictionaryEntriesAction$, rederiveNamesAction$, removeDictionaryEntriesAction$, setColumnDomainAction$, setColumnLogicalNameAction$, setColumnLogicalNamesAction$, setTableLogicalNameAction$, sortDictionaryAction$, updateDictionaryEntryAction$ };
